import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Building2, ShieldCheck } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { CompanyLogo } from "@/components/TeamBadge";
import { CollectorChips, collectorName } from "@/components/company/CompanyTeamCard";

const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

const Info = ({ label, value }) => (
  <div><div className="text-[11px] uppercase tracking-wide text-[#8A9A86]">{label}</div><div className="text-sm text-[#1E3F33] mt-0.5 break-words">{value || "—"}</div></div>
);

const SubscriptionAdmin = ({ id, company, billing, onChange }) => {
  const [busy, setBusy] = useState(false);
  const set = async (active) => {
    setBusy(true);
    try { const { data } = await api.put(`/admin/companies/${id}/subscription`, { active, days: 30 }); onChange(data); toast.success(active ? "Subscription activated for 30 days" : "Subscription deactivated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-soft" data-testid="admin-company-subscription">
      <h2 className="font-heading text-xl text-[#1E3F33] mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2D6A4F]"/> Subscription</h2>
      <div className="grid grid-cols-2 gap-4">
        <Info label="Status" value={company.subscription_active ? (company.subscription_cancelled ? "Active (cancelling)" : "Active") : "Inactive"} />
        <Info label="Paid through" value={fmt(company.subscription_until)} />
        <Info label="Collectors" value={`${billing.seats} × $${billing.price_per_seat.toFixed(2)}`} />
        <Info label="Monthly total" value={`$${billing.monthly_total.toFixed(2)}`} />
      </div>
      <div className="flex gap-2 mt-4">
        <button disabled={busy} onClick={() => set(true)} data-testid="admin-company-activate" className="btn-primary text-xs py-2">Activate 30 days</button>
        {company.subscription_active && <button disabled={busy} onClick={() => set(false)} data-testid="admin-company-deactivate" className="btn-secondary text-xs py-2 text-[#B91C1C]">Deactivate</button>}
      </div>
      <p className="text-[11px] text-[#8A9A86] mt-2">Manual override — Stripe billing is unaffected.</p>
    </div>
  );
};

export default function AdminCompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/admin/companies/${id}`).then(({ data }) => setD(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [id]);
  if (!d) return <div className="container-x py-20 text-[#5C6B64]">Loading…</div>;
  const { company, collectors, billing, account, agreements } = d;

  return (
    <div className="container-x py-12" data-testid="admin-company-detail">
      <AdminTabs />
      <button onClick={() => navigate("/admin/companies")} className="text-sm text-[#5C6B64] hover:text-[#1E3F33] inline-flex items-center gap-1.5 mb-4" data-testid="admin-company-back"><ArrowLeft className="w-4 h-4"/> Back to companies</button>
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-3 mb-8">{company.logo_url ? <CompanyLogo logo={company.logo_url} name={company.company_name} size="w-12 h-12" testid="admin-company-detail-logo" /> : <Building2 className="w-6 h-6 text-[#2D6A4F]"/>} {company.company_name}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-6">
          <div className="card-soft">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-4">Company details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Info label="Username" value={account?.username} />
              <Info label="EIN" value={company.ein} />
              <Info label="Email" value={company.email} />
              <Info label="Phone" value={company.phone} />
              <Info label="Contact person" value={company.contact_name} />
              <Info label="Address" value={[company.address, company.city, company.state, company.zip_code].filter(Boolean).join(", ")} />
              <Info label="Registered" value={fmt(company.created_at)} />
            </div>
          </div>
          <div className="card-soft" data-testid="admin-company-collectors">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-2">Collectors ({collectors.length})</h2>
            {collectors.length === 0 ? <p className="text-sm text-[#8A9A86]">No collectors listed.</p> : (
              <ul className="divide-y divide-[#1E3F33]/8">
                {collectors.map((c) => (
                  <li key={c.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-company-collector-${c.id}`}>
                    <div><div className="font-medium text-[#1E3F33]">{collectorName(c)}</div><div className="text-xs text-[#8A9A86]">{[c.phone, c.email].filter(Boolean).join(" · ")}</div><div className="mt-1"><CollectorChips c={c} /></div></div>
                    <Link to={`/admin/collectors/${c.id}`} className="btn-secondary text-xs py-1.5 px-3" data-testid={`admin-company-collector-open-${c.id}`}>Open</Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="lg:col-span-5 space-y-6">
          <SubscriptionAdmin id={id} company={company} billing={billing} onChange={(data) => setD((p) => ({ ...p, ...data }))} />
          <div className="card-soft" data-testid="admin-company-agreements">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-3">Agreements</h2>
            <ul className="space-y-2">
              {agreements.map((a, i) => (
                <li key={i} className="text-sm"><div className="text-[#1E3F33]">{a.title}</div><div className="text-xs text-[#8A9A86]">v{a.version} · {fmt(a.accepted_at)}{a.ip ? ` · ${a.ip}` : ""}</div></li>
              ))}
              {agreements.length === 0 && <li className="text-sm text-[#8A9A86]">None recorded.</li>}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
